import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoxContext = createContext(null);

export const BoxProvider = ({ children }) => {
  const [boxSizes, setBoxSizes] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [boxSize, setBoxSize] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBoxSizes();
  }, []);

  const fetchBoxSizes = async () => {
    try {
      const response = await axios.get(`${API}/box-sizes`);
      const sizes = response.data;
      setBoxSizes(sizes);
      // Set default box size to the smallest one
      if (sizes.length > 0 && boxSize === null) {
        setBoxSize(sizes[0].size);
      }
    } catch (error) {
      console.error('Error fetching box sizes:', error);
      // Fallback to defaults if API fails
      const fallback = [
        { size: 3, price: 2.99, name: "Starter Box" },
        { size: 5, price: 3.99, name: "Discovery Box" },
      ];
      setBoxSizes(fallback);
      setBoxSize(3);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = useCallback((product) => {
    setSelectedProducts((prev) => {
      if (prev.length >= boxSize) return prev;
      if (prev.find((p) => p.id === product.id)) return prev;
      return [...prev, product];
    });
  }, [boxSize]);

  const removeProduct = useCallback((productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  const isProductSelected = useCallback((productId) => {
    return selectedProducts.some((p) => p.id === productId);
  }, [selectedProducts]);

  const changeBoxSize = useCallback((newSize) => {
    setBoxSize(newSize);
    // If new size is smaller, trim products
    setSelectedProducts((prev) => prev.slice(0, newSize));
  }, []);

  const clearBox = useCallback(() => {
    setSelectedProducts([]);
  }, []);

  const getCurrentBoxInfo = useCallback(() => {
    return boxSizes.find((b) => b.size === boxSize) || boxSizes[0] || { size: 3, price: 2.99, name: "Sample Box" };
  }, [boxSize, boxSizes]);

  const isFull = boxSize !== null && selectedProducts.length >= boxSize;
  const isEmpty = selectedProducts.length === 0;
  const progress = boxSize ? (selectedProducts.length / boxSize) * 100 : 0;

  const value = {
    selectedProducts,
    boxSize,
    boxSizes,
    customerInfo,
    setCustomerInfo,
    addProduct,
    removeProduct,
    isProductSelected,
    changeBoxSize,
    clearBox,
    getCurrentBoxInfo,
    isFull,
    isEmpty,
    progress,
    loading,
  };

  return <BoxContext.Provider value={value}>{children}</BoxContext.Provider>;
};

export const useBox = () => {
  const context = useContext(BoxContext);
  if (!context) {
    throw new Error('useBox must be used within a BoxProvider');
  }
  return context;
};
